import './LocalizationWrapper.scss';
import { User, UserQuery, useUserLazyQuery } from 'generated/graphql';
import { getAccessToken, logoutCleanup } from 'helpers/auth';
import {
  getLocalisationLanguage,
  getSupportedLocaleFromLanguageCode,
} from 'helpers/locales';
import { persistLocalePreference } from 'helpers/localePreference';
import { useUpdateMyLocalePreference } from 'hooks/useUpdateMyLocalePreference';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import {
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';

export const LANGUAGE_LOCAL_STORAGE_KEY = 'lng';

const LocalizationWrapper = ({ children }: { children: ReactNode }) => {
  const { i18n } = useTranslation();
  const { handleMsgType } = useNotification();
  const { updateMyLocalePreference } = useUpdateMyLocalePreference();
  const hasSyncedLocaleRef = useRef(false);

  const [isLoading, setIsLoading] = useState(true);

  const setLanguage = (user: User) => {
    try {
      // Keep the persisted preference in sync so the Apollo link can apply an
      // Accept-Language override only when the user explicitly selected one.
      if (user?.preferredLocale) {
        persistLocalePreference(
          user.preferredLocale.source,
          user.preferredLocale.locale
        );
      }
      // Check for language passed via URL query param (e.g. from auth-client redirect)
      const urlParams = new URLSearchParams(window.location.search);
      const urlLang = urlParams.get('lng');
      const localStorageLang = localStorage.getItem(LANGUAGE_LOCAL_STORAGE_KEY);

      if (urlLang) {
        localStorage.setItem(LANGUAGE_LOCAL_STORAGE_KEY, urlLang);
        i18n.changeLanguage(urlLang);
      } else if (localStorageLang) {
        i18n.changeLanguage(localStorageLang);
      } else if (user) {
        i18n.changeLanguage(getLocalisationLanguage(user.preferredLocale.locale));
      }

      // The user may have picked a language before signing in (the picker only
      // writes localStorage/i18n when logged out), so the backend can lag
      // behind the frontend — push the explicit selection to the BE once.
      const effectiveLanguage = urlLang ?? localStorageLang;
      if (effectiveLanguage && !hasSyncedLocaleRef.current) {
        const targetLocale =
          getSupportedLocaleFromLanguageCode(effectiveLanguage);
        if (
          targetLocale &&
          user?.preferredLocale &&
          targetLocale !== user.preferredLocale.locale
        ) {
          hasSyncedLocaleRef.current = true;
          updateMyLocalePreference(targetLocale).catch(error =>
            Sentry.captureException(error)
          );
        }
      }

      setIsLoading(false);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  const [fetchUser] = useUserLazyQuery({
    onCompleted: (data: UserQuery) => {
      // Intercom boots in IntercomLiveChatGate, once the company context of
      // the current page is known.
      setLanguage(data.User as User);
      Sentry.setUser({ subId: data.User.subId });
    },
    onError: error => {
      handleMsgType({
        type: TNotification.error,
        message: error?.message,
      });
      logoutCleanup();
    },
  });

  useEffect(() => {
    if (getAccessToken()) {
      fetchUser();
    } else {
      setIsLoading(false);
    }
    //eslint-disable-next-line
  }, []);

  if (isLoading) {
    return null;
  }

  return <>{children}</>;
};

export default LocalizationWrapper;
